import { t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { Badge } from '@repo/ui/badge';
import { cn } from '@repo/ui/lib/utils'; // Standard shadcn utility
import { useMemo } from 'react';

type BadgeType = 'high' | 'medium' | 'low' | 'empty';

interface BadgeRow {
  id: number;
  badges: BadgeType[];
}

interface BadgesBackgroundProps {
  /** Total number of rows extending down the page */
  rowsCount?: number;
  /** Fixed width of items per row to maintain full structural layout */
  badgesPerRow?: number;
}

export function BadgesBackground({ rowsCount = 11, badgesPerRow = 15 }: BadgesBackgroundProps) {
  useLocale();

  const dynamicRows = useMemo<BadgeRow[]>(() => {
    const activeTypes: Exclude<BadgeType, 'empty'>[] = ['high', 'medium', 'low'];
    const generatedRows: BadgeRow[] = [];

    for (let r = 0; r < rowsCount; r++) {
      // As r increases, emptyProbability goes from 0% at the top close to 85%-90% at the bottom
      const emptyProbability = (r / (rowsCount - 1)) * 0.4;

      const rowBadges: BadgeType[] = Array.from({ length: badgesPerRow }, () => {
        // Decide randomly if this slot should be an empty space
        if (Math.random() < emptyProbability) {
          return 'empty';
        }
        const activeType = activeTypes[Math.floor(Math.random() * activeTypes.length)];
        if (!activeType) return 'empty';
        // Otherwise, assign a functional badge type randomly
        return activeType;
      });

      generatedRows.push({ id: r, badges: rowBadges });
    }
    return generatedRows;
  }, [getLocale(), rowsCount, badgesPerRow]);

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: '-252px',
          left: '-438px',
          borderRadius: '50%',
          width: '1240px',
          height: '100%',
          zIndex: '1',
          opacity: 1,
          background: 'radial-gradient(50% 50% at 50% 50%, #CDCDE4 0%, #E9ECFB 100%)',
          overflow: 'hidden',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '44px',
          left: '1109px',
          borderRadius: '50%',
          width: '1240px',
          height: '100%',
          zIndex: '1',
          opacity: 1,
          background: 'radial-gradient(50% 50% at 50% 50%, #CDCDE4 0%, #E9ECFB 100%)',
          overflow: 'hidden',
        }}
      />
      <div className="pointer-events-none absolute inset-0 z-10 w-full select-none overflow-hidden pt-4">
        {/* Centered flex container */}
        <div className="flex w-full flex-col items-center gap-1">
          {dynamicRows.map((row) => (
            <div
              className="pointer-events-auto flex max-h-8.5 w-full flex-wrap justify-center gap-0.5 overflow-hidden"
              key={row.id}
            >
              {row.badges.map((type, badgeIdx) => {
                // Completely hide empty spaces - no text, background, or borders
                if (type === 'empty') {
                  return (
                    <Badge
                      aria-hidden="true"
                      className="invisible w-16 px-12 py-4"
                      key={badgeIdx}
                    />
                  );
                }

                return (
                  <Badge
                    className={cn(
                      'inline-flex w-16 cursor-default items-center justify-center rounded-full border px-12 py-4 font-semibold text-xs tracking-wide transition-all duration-200',
                      'border-transparent text-transparent',
                      `bg-[#F2EEF2]`,
                      type === 'high' &&
                        'hover:border-[#E7D0D9] hover:bg-[#E7D0D9] hover:text-[#783A52]',
                      type === 'medium' &&
                        'hover:border-[#E8E0CE] hover:bg-[#E8E0CE] hover:text-[#7D6636]',
                      type === 'low' &&
                        'hover:border-[#D0E7E2] hover:bg-[#D0E7E2] hover:text-[#3A786C]',
                    )}
                    key={badgeIdx}
                  >
                    {type === 'high' && t('reports.high')}
                    {type === 'medium' && t('reports.medium')}
                    {type === 'low' && t('reports.low')}
                  </Badge>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
